import { FC, ReactNode, useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'

interface Props {
  aberto: boolean
  titulo: string
  /** O que o usuário precisa digitar, idêntico (ex.: o código do expurgo). */
  codigo: string
  descricao?: ReactNode
  rotuloConfirmar: string
  /** Campo de texto adicional obrigatório (ex.: motivo da rejeição). */
  pedirMotivo?: { rotulo: string; maximo?: number }
  processando?: boolean
  corConfirmar?: 'primary' | 'error' | 'warning'
  onCancelar: () => void
  onConfirmar: (motivo: string) => void
}

/**
 * Confirmação digitando o código (AN-12 §5.2 "Na UI, confirmação digitando o código do
 * expurgo"). Ação irreversível em potencial: o botão só habilita com o código exato.
 */
export const ConfirmacaoPorCodigoDialog: FC<Props> = ({
  aberto,
  titulo,
  codigo,
  descricao,
  rotuloConfirmar,
  pedirMotivo,
  processando = false,
  corConfirmar = 'primary',
  onCancelar,
  onConfirmar,
}) => {
  const [digitado, setDigitado] = useState('')
  const [motivo, setMotivo] = useState('')

  useEffect(() => {
    if (aberto) {
      setDigitado('')
      setMotivo('')
    }
  }, [aberto])

  const codigoOk = digitado.trim() === codigo
  const motivoOk = !pedirMotivo || motivo.trim().length > 0

  return (
    <Dialog open={aberto} onClose={processando ? undefined : onCancelar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{titulo}</DialogTitle>
      <DialogContent>
        {descricao && <Typography variant="body2" component="div" sx={{ mb: 2 }}>{descricao}</Typography>}
        {pedirMotivo && (
          <TextField
            label={pedirMotivo.rotulo}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            inputProps={{ maxLength: pedirMotivo.maximo ?? 500 }}
            sx={{ mb: 2 }}
          />
        )}
        <Typography variant="body2" sx={{ mb: 1 }}>
          Para confirmar, digite <strong>{codigo}</strong>:
        </Typography>
        <TextField
          fullWidth
          value={digitado}
          onChange={(e) => setDigitado(e.target.value)}
          placeholder={codigo}
          inputProps={{ 'aria-label': 'Código de confirmação' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancelar} disabled={processando}>
          Voltar
        </Button>
        <Button
          variant="contained"
          color={corConfirmar}
          disabled={!codigoOk || !motivoOk || processando}
          onClick={() => onConfirmar(motivo.trim())}
        >
          {processando ? 'Processando…' : rotuloConfirmar}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmacaoPorCodigoDialog
